import React, { useState, useEffect } from 'react';
import './AISettings.css';
import { apiService } from '../services/apiWrapper';
import api, { API_BASE_URL, AI_SERVICE_BASE_URL } from '../services/api';

// Конфигурация на URL – AI и backend идват от api.js (в production – runtime origin)
const AI_SERVICE_URL = AI_SERVICE_BASE_URL;
const BACKEND_API_URL = API_BASE_URL.replace(/\/api\/?$/, '') || 
  (process.env.NODE_ENV === 'production' ? 'https://app.firesguard.net' : 'http://localhost:5000');

// Object types and typical zones/floors (English)
const OBJECT_TYPES = [
  {
    value: 'office',
    label: 'Office building',
    zones: [
      'Ground floor / Reception',
      'Floor 1 – Offices',
      'Floor 2 – Offices',
      'Floor 3+ – Offices',
      'Conference rooms',
      'Server room / IT',
      'Archive / Document storage',
      'Underground parking',
      'Technical rooms'
    ]
  },
  {
    value: 'warehouse',
    label: 'Warehouse',
    zones: [
      'Goods receiving area',
      'Dispatch area',
      'High-rack warehouse',
      'Cold / freezer storage',
      'Hazardous materials storage',
      'Packing area',
      'Office',
      'Staff kitchen / canteen'
    ]
  },
  {
    value: 'production',
    label: 'Production facility',
    zones: [
      'Production hall 1',
      'Production hall 2',
      'Assembly line',
      'Quality control',
      'Finished goods warehouse',
      'Raw materials warehouse',
      'Technical rooms / Boiler room',
      'Administrative office'
    ]
  },
  {
    value: 'hotel',
    label: 'Hotel',
    zones: [
      'Lobby and reception',
      'Guest floors',
      'Conference rooms',
      'Restaurant',
      'Kitchen',
      'SPA / Pool',
      'Underground parking',
      'Staff premises'
    ]
  },
  {
    value: 'retail',
    label: 'Shop / Retail',
    zones: [
      'Sales floor',
      'Store warehouse',
      'Checkout / Exit area',
      'Office',
      'Staff room / Kitchen',
      'Loading area'
    ]
  },
  {
    value: 'mall',
    label: 'Shopping centre / Mall',
    zones: [
      'Common areas and corridors',
      'Retail units / Shops',
      'Food court / Restaurants',
      'Cinema / Entertainment',
      'Underground parking',
      'Technical rooms',
      'Storage areas'
    ]
  },
  {
    value: 'logistics',
    label: 'Logistics centre',
    zones: [
      'Loading docks',
      'Warehouse zones (A / B / C)',
      'Cold storage zones',
      'Office area',
      'Truck parking',
      'Technical rooms'
    ]
  },
  {
    value: 'mixed-office',
    label: 'Mixed office and retail building',
    zones: [
      'Ground floor – Shops',
      'Upper floors – Offices',
      'Underground parking',
      'Technical rooms'
    ]
  }
];

function AISettings() {
  const [activeSection, setActiveSection] = useState('ai'); // 'ai' or 'devices'
  
  // AI Providers state
  const [providers, setProviders] = useState({
    openai: { enabled: false, apiKey: '', model: 'gpt-4' },
    anthropic: { enabled: false, apiKey: '', model: 'claude-3-sonnet-20240229' },
    google: { enabled: false, apiKey: '', model: 'gemini-pro' },
    ollama: { enabled: false, apiKey: '', model: 'mistral', baseURL: '' }
  });
  
  const [activeProvider, setActiveProvider] = useState(null); // null означава никой провайдър не е избран
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ message: '', type: '' });
  const [aiStatus, setAiStatus] = useState({ connected: false, currentProvider: null });

  // Device Connection state
  const [devices, setDevices] = useState([]);
  const [connectedDevices, setConnectedDevices] = useState([]);
  const [deviceScanStatus, setDeviceScanStatus] = useState({ scanning: false, found: 0 });
  const [deviceSettings, setDeviceSettings] = useState({
    autoConnect: true,
    scanInterval: 30,
    connectionTimeout: 10
  });
  // Централен панел за ръчно свързване
  const [centralLinkType, setCentralLinkType] = useState('electrical-panel');
  const [centralLinkEquipmentId, setCentralLinkEquipmentId] = useState('');
  const [centralObjectType, setCentralObjectType] = useState('office');
  const [centralObjectZone, setCentralObjectZone] = useState('');

  // Monitor state - за противопожарна техника от мониторинга
  const [electricalPanels, setElectricalPanels] = useState([]);
  const [sprinklerZones, setSprinklerZones] = useState([]);
  const [evacDoors, setEvacDoors] = useState([]);
  const [smokeDetectors, setSmokeDetectors] = useState([]);
  const [heatSensors, setHeatSensors] = useState([]);
  
  // Device linking modal
  const [linkingModal, setLinkingModal] = useState({ 
    open: false, 
    equipment: null, 
    equipmentType: null,
    availableDevices: []
  });
  
  // Device creation modal
  const [deviceCreationModal, setDeviceCreationModal] = useState({
    open: false,
    forLinking: false,
    equipmentData: null
  });
  
  // New device form data
  const [newDevice, setNewDevice] = useState({
    name: '',
    type: 'smoke_detector',
    location: '',
    ip_address: '',
    connection_type: 'wifi',
    installation_date: new Date().toISOString().split('T')[0],
    status: 'online',
    model: '',
    serial_number: '',
    battery_level: 100,
    notes: ''
  });
  
  // Device search/filter in linking modal
  const [deviceSearchTerm, setDeviceSearchTerm] = useState('');

  // Информационен модал за различните видове техника
  const [infoModal, setInfoModal] = useState({
    open: false,
    type: null,
    title: '',
    description: '',
    body: ''
  });

  const openEquipmentInfo = (type) => {
    if (type === 'smoke') {
      setInfoModal({
        open: true,
        type,
        title: '🔥 Smoke detectors',
        description: 'Smoke detector information',
        body: 'Smoke detectors are managed as individual devices (smoke_detector). Their connection to fire panels and zones is configured centrally during system deployment.'
      });
    } else if (type === 'heat') {
      setInfoModal({
        open: true,
        type,
        title: '🌡️ Heat sensors',
        description: 'Heat sensor information',
        body: 'Heat sensors monitor critical points – electrical panels, rooms and equipment. Their logical connection to panels/zones is set by the system administrator.'
      });
    } else if (type === 'camera') {
      setInfoModal({
        open: true,
        type,
        title: '📷 Cameras',
        description: 'Camera information',
        body: 'Cameras are integrated per project (ONVIF, RTSP, etc.). FireGuard can display video on alarm or inspection depending on the specific integration.'
      });
    } else if (type === 'door-sensor') {
      setInfoModal({
        open: true,
        type,
        title: '🚪 Door sensors',
        description: 'Door sensor information',
        body: 'Door sensors (magnetic contacts, reed switches) report open/closed state of emergency and fire doors. Connection to the system is done during deployment.'
      });
    } else if (type === 'alarm-system') {
      setInfoModal({
        open: true,
        type,
        title: '🚨 Alarm systems',
        description: 'Alarm system information',
        body: 'Security and fire alarm panels connect to FireGuard via contacts, relay outputs or protocols (Modbus, TCP/IP). Configuration is done per project for each client.'
      });
    } else if (type === 'electrical-panel' || type === 'sprinkler-zone' || type === 'evac-door') {
      // За табла / зони / врати – инфо + опция да се отвори прозореца за свързване
      let title = '';
      let body = '';

      if (type === 'electrical-panel') {
        title = '⚡ Electrical panels';
        body =
          'Electrical panels connect to IoT devices for current, temperature and power supply status. From this window you can select a panel and link it to an available device.';
      } else if (type === 'sprinkler-zone') {
        title = '💧 Sprinkler zones';
        body =
          'Sprinkler zones are linked to devices that provide pressure, pump and valve status. After confirmation the manual linking window will open.';
      } else {
        title = '🚪 Evacuation doors';
        body =
          'Evacuation doors connect to devices that monitor lock state, obstructions and forced opening. You can select a door and link it to a device.';
      }

      setInfoModal({
        open: true,
        type,
        title,
        description: 'Manual linking information',
        body
      });
    }
  };

  // Бърз избор на тип техника за свързване (работи за табла / спринклер / врати)
  const quickLinkByType = async (type) => {
    const getListForType = () => {
      if (type === 'electrical-panel') return { list: electricalPanels, key: 'electrical-panel' };
      if (type === 'sprinkler-zone') return { list: sprinklerZones, key: 'sprinkler-zone' };
      if (type === 'evac-door') return { list: evacDoors, key: 'evac-door' };
      return { list: [], key: '' };
    };

    // Първо опитай с текущо заредените данни
    let { list, key: equipmentTypeKey } = getListForType();

    // Ако няма данни, опитай да презаредиш мониторинга
    if (!list || list.length === 0) {
      await loadMonitoringEquipment();
      ({ list, key: equipmentTypeKey } = getListForType());
    }

    if (!list || list.length === 0) {
      setStatus({
        message: 'No equipment of this type is registered for linking. Add it in the system first.',
        type: 'info'
      });
      return;
    }

    // Избери първата несвързана техника, ако има, иначе първата в списъка
    const target = list.find(item => !item.device_id) || list[0];
    openLinkingModal(target, equipmentTypeKey);
  };

  useEffect(() => {
    loadSettings();
    checkAIStatus();
    loadDevices();
    loadMonitoringEquipment();
  }, []);

  const loadMonitoringEquipment = async () => {
    console.log('🔄 Зареждане на противопожарна техника...');
    try {
      // Зареди всички типове противопожарна техника паралелно
      const [panelsRes, zonesRes, doorsRes, devicesRes] = await Promise.all([
        apiService.electricalPanelMonitor.getPanels().catch(() => ({ data: { panels: [] } })),
        apiService.sprinklerMonitor.getZones().catch(() => ({ data: { zones: [] } })),
        apiService.evacDoorMonitor.getDoors().catch(() => ({ data: { doors: [] } })),
        apiService.devices.getAll().catch(() => ({ data: { devices: [] } }))
      ]);
      
      console.log('📊 Получени данни:', {
        panels: panelsRes?.data?.panels?.length || 0,
        zones: zonesRes?.data?.zones?.length || 0,
        doors: doorsRes?.data?.doors?.length || 0
      });
      
      if (panelsRes?.data?.panels) {
        console.log('⚡ Зареждане на електрически табла:', panelsRes.data.panels);
        setElectricalPanels(panelsRes.data.panels);
      }
      if (zonesRes?.data?.zones) {
        console.log('💧 Зареждане на спринклерни зони:', zonesRes.data.zones);
        setSprinklerZones(zonesRes.data.zones);
      }
      if (doorsRes?.data?.doors) {
        console.log('🚪 Зареждане на евакуационни врати:', doorsRes.data.doors);
        setEvacDoors(doorsRes.data.doors);
      }
      
      // Филтрирай детектори и сензори от устройствата
      if (devicesRes?.data) {
        // devicesAPI.getAll() връща { data: { devices: [...] } } или { data: { data: [...] } }
        const allDevices = devicesRes.data.devices || devicesRes.data.data || devicesRes.data;
        console.log('📱 Всички устройства:', allDevices);
        
        if (Array.isArray(allDevices)) {
          setSmokeDetectors(allDevices.filter(d => 
            d.device_type === 'smoke_detector' || d.type === 'smoke_detector'
          ));
          setHeatSensors(allDevices.filter(d => 
            d.device_type === 'heat_sensor' || d.type === 'heat_sensor'
          ));
        }
      }
    } catch (error) {
      console.error('Error loading fire equipment:', error);
    }
  };

  const loadDevices = async () => {
    try {
      const response = await fetch('/api/devices');
      if (response.ok) {
        const data = await response.json();
        setDevices(data.data || []);
        setConnectedDevices(data.data?.filter(d => d.status === 'online') || []);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const scanForDevices = async () => {
    setDeviceScanStatus({ scanning: true, found: 0 });
    setStatus({ message: 'Scanning for devices...', type: 'info' });
    
    try {
      // Simulate device scanning
      for (let i = 0; i < 10; i++) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setDeviceScanStatus(prev => ({ ...prev, found: i + 1 }));
      }
      
      // Refresh device list after scan
      await loadDevices();
      
      // Автоматично свързване ако е включено
      if (deviceSettings.autoConnect) {
        await autoLinkAfterScan();
        setStatus({ message: 'Scan and auto-link completed!', type: 'success' });
      } else {
        setStatus({ message: 'Scan completed successfully!', type: 'success' });
      }
    } catch (error) {
      setStatus({ message: 'Error scanning for devices', type: 'error' });
    } finally {
      setDeviceScanStatus({ scanning: false, found: 0 });
    }
  };

  const connectDevice = async (deviceId) => {
    try {
      const response = await fetch(`/api/devices/${deviceId}/connect`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setStatus({ message: 'Device linked successfully!', type: 'success' });
        loadDevices();
      } else {
        setStatus({ message: 'Error linking device', type: 'error' });
      }
    } catch (error) {
      setStatus({ message: 'Error linking device', type: 'error' });
    }
  };

  const disconnectDevice = async (deviceId) => {
    try {
      const response = await fetch(`/api/devices/${deviceId}/disconnect`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setStatus({ message: 'Device unlinked successfully!', type: 'success' });
        loadDevices();
      } else {
        setStatus({ message: 'Error unlinking device', type: 'error' });
      }
    } catch (error) {
      setStatus({ message: 'Error unlinking device', type: 'error' });
    }
  };

  // Отвори модален диалог за свързване на техника с устройство
  const openLinkingModal = (equipment, equipmentType) => {
    console.log('🔗 Opening linking modal for:', equipment, equipmentType);
    setLinkingModal({
      open: true,
      equipment,
      equipmentType,
      availableDevices: devices.filter(d => d.status === 'online' || d.status === 'active')
    });
    setDeviceSearchTerm(''); // Reset search
  };
  
  // Отвори модален диалог за създаване на устройство
  const openDeviceCreationModal = (forLinking = false, equipmentData = null) => {
    console.log('➕ Opening device creation modal, forLinking:', forLinking, 'equipmentData:', equipmentData);
    
    // Ако идва от панела "Свързване на техника", скрий този панел, но ЗАПАЗИ избраната техника в state
    // Така панелът за свързване ще изчезне визуално, а информацията за техниката ще остане,
    // за да може след създаване да се извърши автоматично свързване.
    if (forLinking) {
      setLinkingModal(prev => ({
        ...prev,
        open: false
      }));
    }

    // Pre-fill location if creating for specific equipment
    if (equipmentData) {
      // Използвай избрания тип обект и зона, за да направиш по-ясно описание на локацията
      const objectTypeConfig = OBJECT_TYPES.find(t => t.value === centralObjectType);
      const objectLabel = objectTypeConfig ? objectTypeConfig.label : '';
      const zoneLabel = centralObjectZone || '';

      const locationParts = [
        objectLabel,
        zoneLabel,
        equipmentData.location
      ].filter(Boolean);

      setNewDevice(prev => ({
        ...prev,
        location: locationParts.join(' | '),
        name: `Device for ${equipmentData.name}`
      }));
    }
    
    setDeviceCreationModal({
      open: true,
      forLinking,
      equipmentData
    });
  };
  
  // Затвори модален диалог за създаване
  const closeDeviceCreationModal = () => {
    console.log('🚪 [MODAL] Затваряне на device creation modal');
    setDeviceCreationModal({
      open: false,
      forLinking: false,
      equipmentData: null
    });
    // Reset form
    setNewDevice({
      name: '',
      type: 'smoke_detector',
      location: '',
      ip_address: '',
      connection_type: 'wifi',
      installation_date: new Date().toISOString().split('T')[0],
      status: 'online',
      model: '',
      serial_number: '',
      battery_level: 100,
      notes: ''
    });
  };

  // Затвори модалния диалог
  const closeLinkingModal = () => {
    console.log('🚪 [MODAL] Затваряне на linking modal');
    setLinkingModal({
      open: false,
      equipment: null,
      equipmentType: null,
      availableDevices: []
    });
  };

  // Свържи техника с устройство
  // Може да се извика с параметри ИЛИ да използва linkingModal state
  const linkEquipmentToDevice = async (deviceId, mode = 'manual', equipmentOverride = null, equipmentTypeOverride = null) => {
    console.log('🔗 [START] linkEquipmentToDevice called with deviceId:', deviceId, 'mode:', mode);
    try {
      // Ако са подадени override параметри, използвай тях, иначе вземи от linkingModal
      const equipment = equipmentOverride || linkingModal.equipment;
      const equipmentType = equipmentTypeOverride || linkingModal.equipmentType;
      
      console.log('🔗 [DEBUG] Equipment data:', { equipment, equipmentType, fromOverride: !!equipmentOverride });
      
      if (!equipment || !equipmentType) {
        console.error('❌ Липсва equipment или equipmentType!', { equipment, equipmentType });
        setStatus({ 
          message: 'Error: Missing equipment data', 
          type: 'error' 
        });
        return;
      }
      
      console.log('🔗 Свързване на техника с устройство:', {
        equipment,
        equipmentType,
        deviceId,
        mode
      });
      
      const token = localStorage.getItem('fireguard_token');
      
      // API заявка за свързване – само към реалния backend
      const response = await fetch(`${BACKEND_API_URL}/api/equipment/${equipmentType}/${equipment.id}/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          device_id: deviceId,
          link_mode: mode
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Свързването е успешно:', result);
        
        setStatus({ 
          message: `${equipment.name} was linked to a device successfully!`, 
          type: 'success' 
        });
        
        // Обнови данните от backend
        await loadMonitoringEquipment();

        // Затвори модалите
        console.log('🔗 [CLOSING] Затваряне на модалите...');
        closeLinkingModal();
        closeDeviceCreationModal();
        console.log('🔗 [CLOSED] Модалите са затворени');
      } else {
        const errorData = await response.json().catch(() => null);
        console.error('❌ Грешка при свързване:', errorData);
        setStatus({ 
          message: `Link error: ${errorData?.message || 'Unknown error'}`, 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('❌ [ERROR] Грешка при свързване на техника:', error);
      console.error('❌ [ERROR] Stack trace:', error.stack);
      setStatus({ 
        message: `Error linking equipment to device: ${error.message}`, 
        type: 'error' 
      });
      // НЕ затваряме модалите при грешка, за да може потребителят да опита отново
    }
  };

  // Създаване на ново устройство
  const createNewDevice = async () => {
    try {
      console.log('➕ Създаване на устройство:', newDevice);
      console.log('🔗 ForLinking:', deviceCreationModal.forLinking);
      console.log('📋 LinkingModal state:', linkingModal);
      
      const token = localStorage.getItem('fireguard_token');
      
      // В production разчитаме само на реалния backend – без mock създаване
      const response = await fetch(`${BACKEND_API_URL}/api/devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(newDevice)
      });

      if (response.ok) {
        const result = await response.json();
        const newDeviceId = result.data?.id || result.id;
        
        console.log('✅ Устройството е създадено:', result);

        setStatus({ 
          message: `Device "${newDevice.name}" was created successfully!`, 
          type: 'success' 
        });

        // Обнови списъка с устройства от backend
        await loadDevices();
        
        // Запази дали е за свързване ПРЕДИ да затвориш модала
        const shouldLink = deviceCreationModal.forLinking && linkingModal.equipment && linkingModal.equipmentType;
        
        // Ако се създава за свързване, свържи автоматично ПРЕДИ да затвориш модалите
        if (shouldLink) {
          console.log('🔗 Автоматично свързване на устройство...');
          await linkEquipmentToDevice(newDeviceId, 'manual');
          // linkEquipmentToDevice вече затваря и двата модала
        } else {
          // Само затвори device creation modal ако не е за свързване
          closeDeviceCreationModal();
        }
      } else {
        const errorData = await response.json();
        setStatus({ 
          message: `Create error: ${errorData.message || 'Unknown error'}`, 
          type: 'error' 
        });
      }
    } catch (error) {
      console.error('Error creating device:', error);
      setStatus({ 
        message: 'Error creating device', 
        type: 'error' 
      });
    }
  };

  // Автоматично свързване след сканиране
  const autoLinkAfterScan = async () => {
    if (!deviceSettings.autoConnect) return;
    
    console.log('🤖 [AUTO-SCAN] Начало на автоматично свързване след сканиране');
    
    try {
      // Автоматично свържи техника без устройства с налични устройства
      const unlinkedPanels = electricalPanels.filter(p => !p.device_id);
      const unlinkedZones = sprinklerZones.filter(z => !z.device_id);
      const unlinkedDoors = evacDoors.filter(d => !d.device_id);
      
      const availableDevices = devices.filter(d => d.status === 'online');
      
      console.log('🤖 [AUTO-SCAN] Намерена несвързана техника:', {
        panels: unlinkedPanels.length,
        zones: unlinkedZones.length,
        doors: unlinkedDoors.length,
        availableDevices: availableDevices.length
      });
      
      let linkCount = 0;
      
      // Smart matching based on device type and location
      // За всеки вид техника, опитай да свържеш с налично устройство
      for (const panel of unlinkedPanels) {
        if (availableDevices[linkCount]) {
          console.log('🤖 [AUTO-SCAN] Свързване на panel:', panel.name, 'с устройство:', availableDevices[linkCount].id);
          // Предай equipment и equipmentType директно като параметри
          await linkEquipmentToDevice(availableDevices[linkCount].id, 'auto', panel, 'electrical-panel');
          linkCount++;
        }
      }
      
      for (const zone of unlinkedZones) {
        if (availableDevices[linkCount]) {
          console.log('🤖 [AUTO-SCAN] Свързване на zone:', zone.name, 'с устройство:', availableDevices[linkCount].id);
          await linkEquipmentToDevice(availableDevices[linkCount].id, 'auto', zone, 'sprinkler-zone');
          linkCount++;
        }
      }
      
      for (const door of unlinkedDoors) {
        if (availableDevices[linkCount]) {
          console.log('🤖 [AUTO-SCAN] Свързване на door:', door.name, 'с устройство:', availableDevices[linkCount].id);
          await linkEquipmentToDevice(availableDevices[linkCount].id, 'auto', door, 'evac-door');
          linkCount++;
        }
      }
      
      setStatus({ 
        message: `Auto-linked ${linkCount} device(s)`, 
        type: 'success' 
      });
      
      console.log('🤖 [AUTO-SCAN] Завърши автоматичното свързване, свързани:', linkCount);
    } catch (error) {
      console.error('❌ [AUTO-SCAN] Грешка при автоматично свързване:', error);
      setStatus({ 
        message: `Auto-link error: ${error.message}`, 
        type: 'error' 
      });
    }
  };
  
  // Филтрирани устройства за свързване
  const filteredDevices = linkingModal.availableDevices.filter(device => {
    if (!deviceSearchTerm) return true;
    
    const searchLower = deviceSearchTerm.toLowerCase();
    return (
      device.name?.toLowerCase().includes(searchLower) ||
      device.device_name?.toLowerCase().includes(searchLower) ||
      device.location?.toLowerCase().includes(searchLower) ||
      device.type?.toLowerCase().includes(searchLower) ||
      device.device_type?.toLowerCase().includes(searchLower)
    );
  });

  const loadSettings = async () => {
    try {
      // Зареждане на ключовете на текущия потребител от backend (всеки клиент свой ключ)
      const res = await api.get('settings/ai-keys');
      if (res.data?.success && res.data.providers) {
        const fromBackend = res.data.providers;
        setProviders(prev => ({
          openai: { ...prev.openai, ...fromBackend.openai },
          anthropic: { ...prev.anthropic, ...fromBackend.anthropic },
          google: { ...prev.google, ...fromBackend.google },
          ollama: { ...prev.ollama, ...fromBackend.ollama }
        }));
      }
      // Статус и активен провайдър от AI услугата (опционално)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const response = await fetch(`${AI_SERVICE_URL}/api/providers`, {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      clearTimeout(timeoutId);
      if (response.ok) {
        const text = await response.text();
        if (/^\s*[{[]/.test(text)) {
          try {
            const data = JSON.parse(text);
            if (data.activeProvider) setActiveProvider(data.activeProvider);
            if (data.providers) {
              setProviders(prev => {
                const keys = ['openai', 'anthropic', 'google', 'ollama'];
                const next = { ...prev };
                for (const k of keys) {
                  if (data.providers[k]) {
                    next[k] = { ...next[k], ...data.providers[k] };
                  }
                }
                return next;
              });
            }
          } catch (_) { /* игнор невалиден JSON */ }
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn('Not logged in – AI keys are saved after login');
      } else if (error.name === 'AbortError') {
        console.warn('AI Service timeout при зареждане на настройки');
      } else if (error.message?.includes('Failed to fetch') || error.code === 'ECONNREFUSED') {
        console.warn('AI Service не е достъпен за статус:', error.message);
      } else {
        console.error('Error loading settings:', error);
      }
    }
  };

  const checkAIStatus = async () => {
    try {
      let userHasKeyFor = {};
      try {
        const keysRes = await api.get('settings/ai-keys');
        if (keysRes.data?.success && keysRes.data.providers) {
          const p = keysRes.data.providers;
          userHasKeyFor = {
            openai: !!(p.openai?.apiKey || p.openai?.enabled),
            anthropic: !!(p.anthropic?.apiKey || p.anthropic?.enabled),
            google: !!(p.google?.apiKey || p.google?.enabled),
            ollama: !!(p.ollama?.apiKey || p.ollama?.enabled)
          };
        }
      } catch (_) {
        // не логваме – потребителят може да не е логнат
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${AI_SERVICE_URL}/api/status`, {
        signal: controller.signal,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      clearTimeout(timeoutId);
      
      const text = await response.text();
      const isJson = /^\s*[{[]/.test(text);
      if (!isJson) {
        setAiStatus({ connected: false, currentProvider: null });
        setStatus({
          message: '❌ AI Service не отговори с JSON (може да не работи на порт 3001 или портът да е зает от друго приложение). Стартирай: cd ai-service && node server.js',
          type: 'error'
        });
        return;
      }
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        setAiStatus({ connected: false, currentProvider: null });
        setStatus({
          message: '❌ AI Service върна невалидни данни. Увери се, че AI сървисът работи на порт 3001.',
          type: 'error'
        });
        return;
      }

      if (response.ok) {
        const isConnected = data.connected || false;
        const currentProvider = data.currentProvider || null;
        const availableFromService = data.health?.available || [];
        const hasUserKey = currentProvider && userHasKeyFor[currentProvider];
        
        setAiStatus({
          connected: isConnected || hasUserKey,
          currentProvider: currentProvider
        });
        
        if (hasUserKey && !availableFromService.includes(currentProvider)) {
          setStatus({
            message: '',
            type: ''
          });
        } else if (currentProvider && !availableFromService.includes(currentProvider) && !hasUserKey) {
          setStatus({
            message: `⚠️ Provider ${currentProvider} is not configured. Please add a valid API key in settings.`,
            type: 'info'
          });
        } else if (!isConnected && currentProvider && !hasUserKey) {
          setStatus({
            message: `❌ Error with ${currentProvider}: Provider is not configured or unavailable. Please check the API key.`,
            type: 'error'
          });
        } else if (isConnected || hasUserKey) {
          setStatus({ message: '', type: '' });
        }
      } else {
        setAiStatus({ connected: false, currentProvider: null });
        setStatus({
          message: `⚠️ AI Service returned an error (${response.status}). Check the logs.`,
          type: 'error'
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('AI Service timeout - може да е бавен или не отговаря');
        setStatus({
          message: '⏱️ AI Service did not respond in time. Check that it is running on port 3001.',
          type: 'error'
        });
      } else if (error.message?.includes('Failed to fetch') || error.code === 'ECONNREFUSED') {
        console.warn('AI Service не е достъпен за проверка на статус:', error.message);
        setStatus({
          message: '❌ AI Service is not available. Make sure it is running on port 3001.',
          type: 'error'
        });
      } else if (error.message?.includes('JSON.parse') || error.name === 'SyntaxError') {
        setStatus({
          message: '❌ AI сървисът не отговори с валидни данни (порт 3001). Стартирай: cd ai-service && node server.js',
          type: 'error'
        });
      } else {
        console.error('Error checking AI status:', error);
        setStatus({
          message: `⚠️ Error checking AI status: ${error.message}`,
          type: 'error'
        });
      }
      setAiStatus({ connected: false, currentProvider: null });
    }
  };

  const handleProviderChange = (provider, field, value) => {
    setProviders(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value
      }
    }));
  };

  const saveSettings = async () => {
    setLoading(true);
    setStatus({ message: '', type: '' });

    try {
      // Запазване на ключовете в backend – по потребител (всеки клиент свой ключ)
      const res = await api.put('settings/ai-keys', { providers });
      if (res.data?.success) {
        if (res.data.providers) setProviders(res.data.providers);
        setStatus({ message: 'Your AI keys were saved successfully.', type: 'success' });
        checkAIStatus();
      } else {
        setStatus({ message: res.data?.message || 'Error saving', type: 'error' });
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setStatus({ message: msg || 'Error saving keys.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const testProvider = async (provider) => {
    setLoading(true);
    setStatus({ message: '', type: '' });

    const config = providers[provider];
    const apiKey = config?.apiKey?.trim();
    const masked = !apiKey || apiKey.startsWith('***');
    if (masked && provider !== 'ollama') {
      setStatus({ message: 'Въведете валиден API ключ (не запазена маскирана стойност) и опитайте отново.', type: 'error' });
      setLoading(false);
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const body =
        masked && provider === 'ollama'
          ? { provider, config: { model: config?.model } }
          : { provider, config: { ...config, apiKey } };

      const response = await fetch(`${AI_SERVICE_URL}/api/test-provider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const text = await response.text();
      let result;
      try {
        result = /^\s*[{[]/.test(text) ? JSON.parse(text) : null;
      } catch (_) {
        setStatus({
          message: `AI сървисът не отговори с JSON. Уверете се, че AI сървисът работи на порт 3001: cd ai-service && node server.js`,
          type: 'error'
        });
        setLoading(false);
        return;
      }

      if (response.ok && result?.success) {
        setStatus({ message: `${getProviderName(provider)} работи коректно.`, type: 'success' });
      } else {
        const errMsg = result?.error || result?.message || response.statusText || `HTTP ${response.status}`;
        setStatus({ message: `${getProviderName(provider)}: ${errMsg}`, type: 'error' });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setStatus({
          message: 'AI сървисът не отговори навремя. Стартирайте AI сървиса: cd ai-service && node server.js',
          type: 'error'
        });
      } else if (error.message?.includes('Failed to fetch') || error.code === 'ECONNREFUSED') {
        setStatus({
          message: `AI сървисът не е достъпен (${AI_SERVICE_URL}). Стартирайте: cd ai-service && node server.js`,
          type: 'error'
        });
      } else {
        setStatus({
          message: `Грешка при тест: ${error.message}`,
          type: 'error'
        });
      }
      console.error('AI Test error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProviderName = (provider) => {
    if (!provider) return 'None';
    const names = {
      openai: 'OpenAI',
      anthropic: 'Anthropic',
      google: 'Google AI',
      ollama: 'Ollama (local)'
    };
    return names[provider] || provider;
  };

  const getProviderIcon = (provider) => {
    const icons = {
      openai: '🤖',
      anthropic: '🧠',
      google: '🔍',
      ollama: '🦙'
    };
    return icons[provider] || '🤖';
  };

  const getModelOptions = (provider) => {
    const models = {
      openai: ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'],
      anthropic: ['claude-3-sonnet-20240229', 'claude-3-haiku-20240307', 'claude-3-opus-20240229'],
      google: ['gemini-pro', 'gemini-pro-vision'],
      ollama: ['mistral', 'llama3', 'llama3.2', 'phi3', 'gemma2', 'qwen2.5']
    };
    const list = models[provider] || [];
    const current = providers[provider]?.model;
    if (current && !list.includes(current)) {
      return [current, ...list];
    }
    return list;
  };

  // Помощни данни за централния панел за свързване
  const getCentralEquipmentList = () => {
    if (centralLinkType === 'electrical-panel') return electricalPanels;
    if (centralLinkType === 'sprinkler-zone') return sprinklerZones;
    if (centralLinkType === 'evac-door') return evacDoors;
    if (centralLinkType === 'smoke-detector') return smokeDetectors;
    if (centralLinkType === 'heat-sensor') return heatSensors;
    // Камери, сензори за врати и алармени системи все още нямат отделни записи за техника – ще се добавят на следващ етап
    return [];
  };
  const centralEquipmentList = getCentralEquipmentList();
  const selectedCentralEquipment = centralEquipmentList.find(eq => String(eq.id) === String(centralLinkEquipmentId));

  return (
    <div className="ai-settings">
      <div className="ai-settings-header">
        <h2>⚙️ System settings</h2>
        <div className="ai-status">
          <span className={`status-indicator ${aiStatus.connected ? 'connected' : 'disconnected'}`}>
            {aiStatus.connected ? '🟢' : '🔴'}
          </span>
          <span className="status-text">
            {aiStatus.connected 
              ? `AI Active: Yes (${getProviderName(aiStatus.currentProvider)})`
              : 'AI Active: No'
            }
          </span>
        </div>
      </div>

      {status.message && (
        <div className={`status-message ${status.type}`}>
          {status.type === 'success' ? '✅' : status.type === 'info' ? '📋' : '❌'} {status.message}
        </div>
      )}

      {/* Main Section Tabs */}
      <div className="main-sections">
        <button 
          className={`section-tab ${activeSection === 'ai' ? 'active' : ''}`}
          onClick={() => setActiveSection('ai')}
        >
          🤖 AI Providers
        </button>
        <button 
          className={`section-tab ${activeSection === 'devices' ? 'active' : ''}`}
          onClick={() => setActiveSection('devices')}
        >
          📡 Fire equipment
        </button>
      </div>

      {/* AI Providers Section */}
      {activeSection === 'ai' && (
        <div className="ai-section">
          <div className="provider-tabs">
        {Object.keys(providers).map(provider => (
          <button
            key={provider}
            className={`provider-tab ${provider === activeProvider ? 'active' : ''}`}
            onClick={() => setActiveProvider(provider === activeProvider ? null : provider)}
          >
            {getProviderIcon(provider)} {getProviderName(provider)}
          </button>
        ))}
      </div>

      {activeProvider && (
      <div className="provider-config">
        <div className="provider-settings">
          <h3>
            {getProviderIcon(activeProvider)} {getProviderName(activeProvider)} settings
          </h3>

          <div className="setting-group">
            <label className="setting-label">
              <input
                type="checkbox"
                checked={providers[activeProvider].enabled}
                onChange={(e) => handleProviderChange(activeProvider, 'enabled', e.target.checked)}
              />
              <span className="checkmark"></span>
              Active provider
            </label>
          </div>

          {activeProvider !== 'ollama' && (
          <div className="setting-group">
            <label className="setting-label">API Key:</label>
            <input
              type="password"
              className="setting-input"
              value={providers[activeProvider].apiKey}
              onChange={(e) => handleProviderChange(activeProvider, 'apiKey', e.target.value)}
              placeholder={`Enter ${getProviderName(activeProvider)} API key...`}
            />
          </div>
          )}

          {activeProvider === 'ollama' && (
          <div className="setting-group">
            <p className="setting-label" style={{ marginBottom: 0 }}>
              Ollama е с отворен код и не използва API ключове. Връзката е към локалния сървър (настройва се в <code>ai-service/.env</code>).
            </p>
          </div>
          )}

          <div className="setting-group">
            <label className="setting-label">Model:</label>
            <select
              className="setting-select"
              value={providers[activeProvider].model}
              onChange={(e) => handleProviderChange(activeProvider, 'model', e.target.value)}
            >
              {getModelOptions(activeProvider).map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>

          <div className="setting-actions">
            <button
              className="btn btn-test"
              onClick={() => testProvider(activeProvider)}
              disabled={
                loading ||
                !providers[activeProvider].enabled ||
                (activeProvider !== 'ollama' && !providers[activeProvider].apiKey)
              }
            >
              {loading ? '⏳ Testing...' : '🧪 Test'}
            </button>
          </div>
        </div>

        <div className="provider-info">
          <h4>📋 Provider information</h4>
          <div className="info-content">
            {activeProvider === 'openai' && (
              <div>
                <p><strong>OpenAI GPT модели:</strong></p>
                <ul>
<li><strong>GPT-4:</strong> Most capable model for complex tasks</li>
                                  <li><strong>GPT-4 Turbo:</strong> Faster and cheaper variant of GPT-4</li>
                                  <li><strong>GPT-3.5 Turbo:</strong> Fast and efficient for basic tasks</li>
                </ul>
                <p><em>Get API key from platform.openai.com</em></p>
              </div>
            )}
            
            {activeProvider === 'anthropic' && (
              <div>
                <p><strong>Anthropic Claude модели:</strong></p>
                <ul>
<li><strong>Claude-3 Sonnet:</strong> Balanced model for general tasks</li>
                                  <li><strong>Claude-3 Haiku:</strong> Fast model for simple tasks</li>
                                  <li><strong>Claude-3 Opus:</strong> Most capable model for complex analysis</li>
                </ul>
                <p><em>Get API key from console.anthropic.com</em></p>
              </div>
            )}

            {activeProvider === 'google' && (
              <div>
                <p><strong>Google AI модели:</strong></p>
                <ul>
<li><strong>Gemini Pro:</strong> Powerful multimodal model</li>
                                  <li><strong>Gemini Pro Vision:</strong> Enhanced for image analysis</li>
                </ul>
                <p><em>Get API key from makersuite.google.com</em></p>
              </div>
            )}

            {activeProvider === 'ollama' && (
              <div>
                <p><strong>Ollama (локален, open source)</strong></p>
                <ul>
                  <li>Не са нужни API ключове — достъпът е до вашия Ollama на машината/сървъра.</li>
                  <li>Адресът се задава в AI услугата: <code>OLLAMA_BASE_URL</code> в <code>ai-service/.env</code> (OpenAI-съвместим <code>/v1</code> endpoint).</li>
                  <li>Моделът трябва да е изтеглен на сървъра, напр. <code>ollama pull mistral</code>.</li>
                </ul>
                {providers.ollama?.baseURL ? (
                  <p>
                    <strong>Base URL (от AI услугата):</strong>{' '}
                    <code>{providers.ollama.baseURL}</code>
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {activeProvider && (
      <div className="settings-footer">
        <button
          className="btn btn-primary btn-save"
          onClick={saveSettings}
          disabled={loading}
        >
          {loading ? '⏳ Saving...' : '💾 Save settings'}
        </button>
        
        <button
          className="btn btn-secondary"
          onClick={checkAIStatus}
          disabled={loading}
        >
          🔄 Refresh status
        </button>
      </div>
      )}
      </div>
      )}

      {/* Device Connection Section */}
      {activeSection === 'devices' && (
        <div className="devices-section">
          <div className="devices-header">
            <h3>📡 Fire equipment management</h3>
            <div className="device-controls">
              <button
                className="btn btn-primary"
                onClick={scanForDevices}
                disabled={deviceScanStatus.scanning}
              >
                {deviceScanStatus.scanning ? '🔍 Scanning...' : '🔍 Scan for devices'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={loadDevices}
              >
                🔄 Refresh list
              </button>
            </div>
          </div>

          {deviceScanStatus.scanning && (
            <div className="scan-progress">
              <div className="scan-info">
<span>🔍 Scan in progress...</span>
                                <span>Found: {deviceScanStatus.found} devices</span>
              </div>
              <div className="scan-animation">
                <div className="scanner"></div>
              </div>
            </div>
          )}

          {/* Device Statistics */}
          <div className="device-stats">
            <div className="stat-card">
              <span className="stat-icon">📊</span>
              <span className="stat-label">Total devices</span>
              <span className="stat-value">{devices.length}</span>
            </div>
            <div className="stat-card connected">
              <span className="stat-icon">🟢</span>
              <span className="stat-label">Linked</span>
              <span className="stat-value">{connectedDevices.length}</span>
            </div>
            <div className="stat-card offline">
              <span className="stat-icon">🔴</span>
              <span className="stat-label">Offline</span>
              <span className="stat-value">{devices.length - connectedDevices.length}</span>
            </div>
          </div>

          {/* Device Types */}
          <div className="device-types">
            <h4>🔧 Fire equipment types</h4>
            <div className="device-type-grid">
              <div className="device-type-card">
                <span className="device-type-icon">💨</span>
                <span className="device-type-name">Smoke detectors</span>
                <span className="device-type-count">{devices.filter(d => d.device_type === 'smoke_detector').length}</span>
              </div>
              <div className="device-type-card">
                <span className="device-type-icon">🌡️</span>
                <span className="device-type-name">Heat sensors</span>
                <span className="device-type-count">{devices.filter(d => d.device_type === 'heat_sensor').length}</span>
              </div>
              <div className="device-type-card">
                <span className="device-type-icon">📷</span>
                <span className="device-type-name">Cameras</span>
                <span className="device-type-count">{devices.filter(d => d.device_type === 'camera').length}</span>
              </div>
              <div className="device-type-card">
                <span className="device-type-icon">🚪</span>
                <span className="device-type-name">Door sensors</span>
                <span className="device-type-count">{devices.filter(d => d.device_type === 'door_sensor').length}</span>
              </div>
              {/* Спринклерни системи - от мониторинга */}
              <div className="device-type-card" style={{ border: sprinklerZones.length > 0 ? '2px solid #4CAF50' : undefined, background: sprinklerZones.length > 0 ? 'rgba(76, 175, 80, 0.1)' : undefined }}>
                <span className="device-type-icon">💧</span>
                <span className="device-type-name">Sprinkler zones</span>
                <span className="device-type-count">{sprinklerZones.length}</span>
                {sprinklerZones.length > 0 && (
                  <div style={{ fontSize: '1.2em', color: '#4CAF50', marginTop: '5px' }}>
                    ✅
                  </div>
                )}
              </div>
              
              {/* Електрически табла - от мониторинга */}
              <div className="device-type-card" style={{ border: electricalPanels.length > 0 ? '2px solid #4CAF50' : undefined, background: electricalPanels.length > 0 ? 'rgba(76, 175, 80, 0.1)' : undefined }}>
                <span className="device-type-icon">⚡</span>
                <span className="device-type-name">Electrical panels</span>
                <span className="device-type-count">{electricalPanels.length}</span>
                {electricalPanels.length > 0 && (
                  <div style={{ fontSize: '1.2em', color: '#4CAF50', marginTop: '5px' }}>
                    ✅
                  </div>
                )}
              </div>
              
              {/* Евакуационни врати - от мониторинга */}
              <div className="device-type-card" style={{ border: evacDoors.length > 0 ? '2px solid #4CAF50' : undefined, background: evacDoors.length > 0 ? 'rgba(76, 175, 80, 0.1)' : undefined }}>
                <span className="device-type-icon">🚪</span>
                <span className="device-type-name">Evacuation doors</span>
                <span className="device-type-count">{evacDoors.length}</span>
                {evacDoors.length > 0 && (
                  <div style={{ fontSize: '1.2em', color: '#4CAF50', marginTop: '5px' }}>
                    ✅
                  </div>
                )}
              </div>
              
              <div className="device-type-card">
                <span className="device-type-icon">🔔</span>
                <span className="device-type-name">Alarm systems</span>
                <span className="device-type-count">{devices.filter(d => d.device_type === 'alarm').length}</span>
          </div>
          </div>
          </div>

          {/* Централен панел за свързване */}
          <div className="central-connection-panel">
            <h4>⚙️ Central panel for manual linking</h4>
            <p
              style={{
                color: '#333',
                fontSize: '0.9rem',
                marginBottom: '16px'
              }}
            >
              Step 1: select object type and zone. Step 2: select equipment (panel / zone / door) and continue to link with a device.
            </p>

            {/* Стъпка 1: Object type и зона */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(220px, 260px) minmax(260px, 1fr)',
                gap: '12px',
                marginBottom: '12px'
              }}
            >
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '0.85rem',
                    color: '#333'
                  }}
                >
                  Object type
                </label>
                <select
                  className="form-input"
                  value={centralObjectType}
                  onChange={(e) => {
                    setCentralObjectType(e.target.value);
                    setCentralObjectZone('');
                  }}
                >
                  {OBJECT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '0.85rem',
                    color: '#333'
                  }}
                >
                  Zone in object (floor / room / storage area)
                </label>
                <select
                  className="form-input"
                  value={centralObjectZone}
                  onChange={(e) => setCentralObjectZone(e.target.value)}
                >
                  <option value="">Select zone...</option>
                  {OBJECT_TYPES.find((t) => t.value === centralObjectType)?.zones.map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Стъпка 2: Тип техника и конкретно табло / зона / врата */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(220px, 260px) minmax(260px, 1fr) auto',
                gap: '12px',
                alignItems: 'flex-end',
                marginBottom: '8px'
              }}
            >
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '0.85rem',
                    color: '#333'
                  }}
                >
                  Тип техника
                </label>
                <select
                  className="form-input"
                  value={centralLinkType}
                  onChange={(e) => {
                    setCentralLinkType(e.target.value);
                    setCentralLinkEquipmentId('');
                  }}
                >
<option value="electrical-panel">⚡ Electrical panels</option>
                                  <option value="sprinkler-zone">💧 Sprinkler zones</option>
                                  <option value="evac-door">🚪 Evacuation doors</option>
                                  <option value="smoke-detector">💨 Smoke detectors</option>
                                  <option value="heat-sensor">🌡️ Heat sensors</option>
                                  <option value="camera">📷 Cameras</option>
                                  <option value="door-sensor">🚪 Door sensors</option>
                                  <option value="alarm-system">🔔 Alarm systems</option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '0.85rem',
                    color: '#333'
                  }}
                >
                  Specific equipment / zone
                </label>
                {centralEquipmentList.length === 0 ? (
                  <div
                    style={{
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px dashed rgba(33, 150, 243, 0.4)',
                      fontSize: '0.85rem',
                      color: '#333',
                      background: 'rgba(255, 255, 255, 0.3)'
                    }}
                  >
                    No equipment of this type registered. Add it first via the monitoring system / backend.
                  </div>
                ) : (
                  <select
                    className="form-input"
                    value={centralLinkEquipmentId}
                    onChange={(e) => setCentralLinkEquipmentId(e.target.value)}
                  >
                    <option value="">Select panel / zone / door...</option>
                    {centralEquipmentList.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} — {item.location || 'No location'}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <button
                  className="btn btn-primary"
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={() => {
                    if (!centralObjectZone) {
                      setStatus({
                        message: 'Please select a zone in the object (floor / room / storage area) before continuing.',
                        type: 'info'
                      });
                      return;
                    }
                    if (!centralLinkEquipmentId) {
                      setStatus({
                        message: 'Please select specific equipment (panel, zone or door) before continuing.',
                        type: 'info'
                      });
                      return;
                    }
                    if (!selectedCentralEquipment) {
                      setStatus({
                        message: 'Selected equipment was not found. Refresh the data and try again.',
                        type: 'error'
                      });
                      return;
                    }
                    openLinkingModal(selectedCentralEquipment, centralLinkType);
                  }}
                  disabled={centralEquipmentList.length === 0}
                >
                  Продължи към свързване
                </button>
              </div>
            </div>

            <p
              style={{
                color: '#333',
                fontSize: '0.8rem',
                marginTop: '4px'
              }}
            >
              After opening the linking window you can select an available device or add a new one
              (with IP address and connection parameters). Object type and zone information can be used
              in the device description/location and for smart monitoring.
            </p>
          </div>

          {/* Connection Settings - временно скрит (ще бъде заменен с централен панел за свързване) */}
          <div className="connection-settings" style={{ display: 'none' }}>
            <h4>⚙️ Equipment connection settings</h4>
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem', marginBottom: '20px' }}>
              Configure how devices are linked automatically or manually to fire equipment
            </p>

            {/* Панел за избор по вид техника и бързо свързване */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
              }}
            >
              {/* Детектори за дим */}
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '10px',
                  padding: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}
              >
                <h5
                  style={{
                    margin: '0 0 8px 0',
                    color: '#333',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  🔥 Smoke detectors
                </h5>
                <p
                  style={{
                    margin: '0 0 6px 0',
                    color: '#333',
                    fontSize: '0.8rem'
                  }}
                >
                  Addressable and conventional smoke detectors for early fire detection.
                </p>
                <p
                  style={{
                    margin: '0 0 10px 0',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}
                >
                  Available devices: {smokeDetectors.length || 0}
                </p>
                <button
                  className="btn btn-primary btn-small"
                  style={{ fontSize: '0.85rem', width: '100%' }}
                  onClick={() => openEquipmentInfo('smoke')}
                >
                  🔗 Link panel to device
                </button>
              </div>

              {/* Температурни сензори */}
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '10px',
                  padding: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}
              >
                <h5
                  style={{
                    margin: '0 0 8px 0',
                    color: '#333',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  🌡️ Heat sensors
                </h5>
                <p
                  style={{
                    margin: '0 0 6px 0',
                    color: '#333',
                    fontSize: '0.8rem'
                  }}
                >
                  Sensors for temperature monitoring in panels, rooms and critical zones.
                </p>
                <p
                  style={{
                    margin: '0 0 10px 0',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}
                >
                  Available devices: {heatSensors.length || 0}
                </p>
                <button
                  className="btn btn-primary btn-small"
                  style={{ fontSize: '0.85rem', width: '100%' }}
                  onClick={() => openEquipmentInfo('heat')}
                >
                  🔗 Link panel to device
                </button>
              </div>

              {/* Камери */}
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '10px',
                  padding: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}
              >
                <h5
                  style={{
                    margin: '0 0 8px 0',
                    color: '#333',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  📷 Камери
                </h5>
                <p
                  style={{
                    margin: '0 0 6px 0',
                    color: '#333',
                    fontSize: '0.8rem'
                  }}
                >
                  Video surveillance of key areas for visual verification on alarm.
                </p>
                <p
                  style={{
                    margin: '0 0 10px 0',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}
                >
                  Available devices: 0
                </p>
                <button
                  className="btn btn-primary btn-small"
                  style={{ fontSize: '0.85rem', width: '100%' }}
                  onClick={() => openEquipmentInfo('camera')}
                >
                  🔗 Link panel to device
                </button>
              </div>

              {/* Сензори за врати */}
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '10px',
                  padding: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}
              >
                <h5
                  style={{
                    margin: '0 0 8px 0',
                    color: '#333',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  🚪 Door sensors
                </h5>
                <p
                  style={{
                    margin: '0 0 6px 0',
                    color: '#333',
                    fontSize: '0.8rem'
                  }}
                >
                  Door contacts and magnetic sensors for open/closed state monitoring.
                </p>
                <p
                  style={{
                    margin: '0 0 10px 0',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}
                >
                  Available devices: 0
                </p>
                <button
                  className="btn btn-primary btn-small"
                  style={{ fontSize: '0.85rem', width: '100%' }}
                  onClick={() => openEquipmentInfo('door-sensor')}
                >
                  🔗 Link panel to device
                </button>
              </div>

              {/* Алармени системи */}
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '10px',
                  padding: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}
              >
                <h5
                  style={{
                    margin: '0 0 8px 0',
                    color: '#333',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  🚨 Alarm systems
                </h5>
                <p
                  style={{
                    margin: '0 0 6px 0',
                    color: '#333',
                    fontSize: '0.8rem'
                  }}
                >
                  Security and fire alarm panels integrated with fire monitoring.
                </p>
                <p
                  style={{
                    margin: '0 0 10px 0',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}
                >
                  Available devices: 0
                </p>
                <button
                  className="btn btn-primary btn-small"
                  style={{ fontSize: '0.85rem', width: '100%' }}
                  onClick={() => openEquipmentInfo('alarm-system')}
                >
                  🔗 Link panel to device
                </button>
              </div>

              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '10px',
                  padding: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}
              >
                <h5
                  style={{
                    margin: '0 0 8px 0',
                    color: '#333',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  ⚡ Electrical panels
                </h5>
                <p
                  style={{
                    margin: '0 0 10px 0',
                    color: '#333',
                    fontSize: '0.8rem'
                  }}
                >
                  Panels for monitoring temperatures, current loads and electrical network status.
                </p>
                <button
                  className="btn btn-primary btn-small"
                  style={{ fontSize: '0.85rem', width: '100%' }}
                  onClick={() => openEquipmentInfo('electrical-panel')}
                >
                  🔗 Link panel to device
                </button>
              </div>

              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '10px',
                  padding: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}
              >
                <h5
                  style={{
                    margin: '0 0 8px 0',
                    color: '#333',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  💧 Спринклерни зони
                </h5>
                <p
                  style={{
                    margin: '0 0 10px 0',
                    color: '#333',
                    fontSize: '0.8rem'
                  }}
                >
                  Water-based fire protection zones – pressure, pump and valve status.
                </p>
                <button
                  className="btn btn-primary btn-small"
                  style={{ fontSize: '0.85rem', width: '100%' }}
                  onClick={() => openEquipmentInfo('sprinkler-zone')}
                >
                  🔗 Link zone to device
                </button>
              </div>

              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '10px',
                  padding: '14px',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}
              >
                <h5
                  style={{
                    margin: '0 0 8px 0',
                    color: '#333',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  🚪 Evacuation doors
                </h5>
                <p
                  style={{
                    margin: '0 0 10px 0',
                    color: '#333',
                    fontSize: '0.8rem'
                  }}
                >
                  Emergency exit and fire doors – lock status, obstructions, open doors.
                </p>
                <button
                  className="btn btn-primary btn-small"
                  style={{ fontSize: '0.85rem', width: '100%' }}
                  onClick={() => openEquipmentInfo('evac-door')}
                >
                  🔗 Link door to device
                </button>
              </div>
            </div>
            
            {/* Режими на свързване */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '20px', 
              marginBottom: '25px' 
            }}>
              {/* Автоматично свързване */}
              <div style={{ 
                background: 'rgba(76, 175, 80, 0.15)', 
                padding: '20px', 
                borderRadius: '12px',
                border: deviceSettings.autoConnect ? '2px solid rgba(76, 175, 80, 0.6)' : '2px solid rgba(76, 175, 80, 0.3)',
                boxShadow: deviceSettings.autoConnect ? '0 0 15px rgba(76, 175, 80, 0.3)' : 'none',
                transition: 'all 0.3s ease'
              }}>
                <h5 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#333', 
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  🤖 Auto-link
                  {deviceSettings.autoConnect && <span style={{ fontSize: '1.2rem' }}>✅</span>}
                </h5>
                <label className="setting-label" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={deviceSettings.autoConnect}
                    onChange={(e) => setDeviceSettings(prev => ({ ...prev, autoConnect: e.target.checked }))}
                  />
                  <span className="checkmark"></span>
                  <span style={{ fontWeight: '600' }}>Enable auto-link</span>
                </label>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem', marginTop: '10px', marginLeft: '0' }}>
                  ✨ Newly discovered devices will be auto-linked to available equipment by location and type
                </p>
                
                {deviceSettings.autoConnect && (
                  <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <label className="setting-label" style={{ fontSize: '0.85rem', marginBottom: '5px' }}>⏱️ Scan interval:</label>
                      <input
                        type="number"
                        className="setting-input"
                        value={deviceSettings.scanInterval}
                        onChange={(e) => setDeviceSettings(prev => ({ ...prev, scanInterval: parseInt(e.target.value) }))}
                        min="5"
                        max="300"
                        style={{ padding: '8px' }}
                      />
                      <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem', marginTop: '3px' }}>
                        {deviceSettings.scanInterval} sec.
                      </p>
                    </div>
                    
                    <div>
                      <label className="setting-label" style={{ fontSize: '0.85rem', marginBottom: '5px' }}>⏳ Connection timeout:</label>
                      <input
                        type="number"
                        className="setting-input"
                        value={deviceSettings.connectionTimeout}
                        onChange={(e) => setDeviceSettings(prev => ({ ...prev, connectionTimeout: parseInt(e.target.value) }))}
                        min="5"
                        max="60"
                        style={{ padding: '8px' }}
                      />
                      <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem', marginTop: '3px' }}>
                        {deviceSettings.connectionTimeout} sec.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Ръчно свързване */}
              <div style={{ 
                background: 'rgba(33, 150, 243, 0.15)', 
                padding: '20px', 
                borderRadius: '12px',
                border: '2px solid rgba(33, 150, 243, 0.4)',
                transition: 'all 0.3s ease'
              }}>
                <h5 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#333', 
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  👆 Manual link
                </h5>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem', marginBottom: '15px' }}>
                  🎯 Изберете точно коя техника да се свърже с кое устройство
                </p>
                
                <div style={{ 
                  background: 'rgba(255, 255, 255, 0.1)', 
                  padding: '12px', 
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <p style={{ margin: '0 0 8px 0', color: '#333', fontSize: '0.9rem', fontWeight: '600' }}>
                    📊 Статус на техниката:
                  </p>
                  <div style={{ display: 'flex', gap: '15px', fontSize: '0.85rem' }}>
                    <div>
                      <span style={{ color: '#4CAF50' }}>✅ Свързани: </span>
                      <strong style={{ color: '#333' }}>
                        {[...electricalPanels, ...sprinklerZones, ...evacDoors].filter(item => item.device_id).length}
                      </strong>
                    </div>
                    <div>
                      <span style={{ color: '#FF9800' }}>⚠️ Несвързани: </span>
                      <strong style={{ color: '#333' }}>
                        {[...electricalPanels, ...sprinklerZones, ...evacDoors].filter(item => !item.device_id).length}
                      </strong>
                    </div>
                  </div>
                </div>
                
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', marginBottom: '12px' }}>
                  За да свържете ръчно техника с устройство:
                </p>
                <ol style={{ 
                  margin: '0', 
                  paddingLeft: '20px', 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontSize: '0.8rem',
                  lineHeight: '1.6'
                }}>
                  <li>Намерете техниката в списъка по-долу</li>
                  <li>Натиснете бутон "🔗 Свържи с устройство"</li>
                  <li>Изберете устройство от списъка</li>
                  <li>Потвърдете свързването</li>
                </ol>
              </div>
            </div>
            
            
            {/* Ръчно управление - Пълен списък с техниката */}
            {[...electricalPanels, ...sprinklerZones, ...evacDoors].length === 0 ? (
              <div
                style={{
                  marginTop: '25px',
                  padding: '20px',
                  background: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: '12px',
                  border: '2px dashed rgba(33, 150, 243, 0.4)',
                  textAlign: 'center'
                }}
              >
                <h5
                  style={{
                    margin: '0 0 10px 0',
                    color: '#333',
                    fontSize: '1.1rem'
                  }}
                >
                  Няма регистрирана противопожарна техника
                </h5>
                <p
                  style={{
                    color: '#333',
                    fontSize: '0.9rem',
                    marginBottom: '4px'
                  }}
                >
                  За да използвате ръчното свързване, първо трябва да добавите реални записи за
                  спринклерни зони, електрически табла и евакуационни врати.
                </p>
                <p
                  style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.85rem'
                  }}
                >
                  Това се прави през backend / администраторския панел (или чрез интеграция със
                  системата за мониторинг).
                </p>
              </div>
            ) : (
              <div style={{ 
                marginTop: '25px', 
                padding: '20px', 
                background: 'rgba(33, 150, 243, 0.1)', 
                borderRadius: '12px',
                border: '2px solid rgba(33, 150, 243, 0.3)'
              }}>
                <h5 style={{ 
                  margin: '0 0 15px 0', 
                  color: '#333', 
                  fontSize: '1.2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  👆 Ръчно управление на свързванията
                </h5>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem', marginBottom: '20px' }}>
                  Управлявайте връзките между техниката и устройствата. Натиснете "🔗 Свържи с устройство" за да свържете или "❌ Прекъсни" за да премахнете връзка.
                </p>
                
                {/* Електрически табла */}
                {electricalPanels.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ 
                      color: '#333', 
                      fontSize: '1rem', 
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      ⚡ Electrical panels ({electricalPanels.length})
                    </h6>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
                      {electricalPanels.map((panel) => (
                        <div key={`manual-panel-${panel.id}`} style={{ 
                          background: panel.device_id ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.05)', 
                          padding: '15px', 
                          borderRadius: '10px',
                          border: panel.device_id ? '2px solid rgba(76, 175, 80, 0.4)' : '2px solid rgba(255, 255, 255, 0.2)',
                          transition: 'all 0.3s ease'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#333', fontWeight: '600', fontSize: '0.95rem', marginBottom: '6px' }}>
                                ⚡ {panel.name}
                              </div>
                              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', marginBottom: '4px' }}>
                                📍 {panel.location || 'Unspecified'}
                              </div>
                              <div style={{ 
                                color: panel.state === 'ALARM' ? '#ff4444' : panel.state === 'SUPERVISORY' ? '#ffaa00' : '#44ff44',
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                              }}>
                                📊 {panel.state || 'NORMAL'}
                              </div>
                            </div>
                            <div style={{ 
                              padding: '4px 10px', 
                              borderRadius: '12px',
                              background: panel.device_id ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              color: panel.device_id ? '#4CAF50' : '#FF9800'
                            }}>
                              {panel.device_id ? '✅ Свързано' : '⚠️ Несвързано'}
                            </div>
                          </div>
                          
                          {panel.device_id && (
                            <div style={{ 
                              background: 'rgba(255, 255, 255, 0.1)', 
                              padding: '8px', 
                              borderRadius: '6px',
                              marginBottom: '10px',
                              fontSize: '0.8rem',
                              color: 'rgba(255, 255, 255, 0.9)'
                            }}>
                              🔗 Свързано с устройство ID: <strong style={{ color: '#333' }}>{panel.device_id}</strong>
                            </div>
                          )}
                          
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {!panel.device_id ? (
                              <button
                                className="btn btn-success btn-small"
                                onClick={() => openLinkingModal(panel, 'electrical-panel')}
                                style={{ flex: 1, fontSize: '0.85rem' }}
                              >
                                🔗 Свържи с устройство
                              </button>
                            ) : (
                              <>
                                <button
                                  className="btn btn-secondary btn-small"
                                  onClick={() => openLinkingModal(panel, 'electrical-panel')}
                                  style={{ flex: 1, fontSize: '0.85rem' }}
                                >
                                  🔄 Смени устройство
                                </button>
                                <button
                                  className="btn btn-danger btn-small"
                                  onClick={async () => {
                                    if (window.confirm(`Are you sure you want to unlink "${panel.name}"?`)) {
                                      // TODO: Implement disconnect functionality
                                      setStatus({ message: 'Unlink feature will be added soon', type: 'info' });
                                    }
                                  }}
                                  style={{ fontSize: '0.85rem' }}
                                >
                                  ❌ Прекъсни
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Спринклерни зони */}
                {sprinklerZones.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ 
                      color: '#333', 
                      fontSize: '1rem', 
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      💧 Спринклерни зони ({sprinklerZones.length})
                    </h6>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
                      {sprinklerZones.map((zone) => (
                        <div key={`manual-zone-${zone.id}`} style={{ 
                          background: zone.device_id ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.05)', 
                          padding: '15px', 
                          borderRadius: '10px',
                          border: zone.device_id ? '2px solid rgba(76, 175, 80, 0.4)' : '2px solid rgba(255, 255, 255, 0.2)',
                          transition: 'all 0.3s ease'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#333', fontWeight: '600', fontSize: '0.95rem', marginBottom: '6px' }}>
                                💧 {zone.name}
                              </div>
                              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', marginBottom: '4px' }}>
                                📍 {zone.location || 'Unspecified'}
                              </div>
                              <div style={{ 
                                color: zone.state === 'ALARM' ? '#ff4444' : zone.state === 'SUPERVISORY' ? '#ffaa00' : '#44ff44',
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                              }}>
                                📊 {zone.state || 'NORMAL'}
                              </div>
                            </div>
                            <div style={{ 
                              padding: '4px 10px', 
                              borderRadius: '12px',
                              background: zone.device_id ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              color: zone.device_id ? '#4CAF50' : '#FF9800'
                            }}>
                              {zone.device_id ? '✅ Свързано' : '⚠️ Несвързано'}
                            </div>
                          </div>
                          
                          {zone.device_id && (
                            <div style={{ 
                              background: 'rgba(255, 255, 255, 0.1)', 
                              padding: '8px', 
                              borderRadius: '6px',
                              marginBottom: '10px',
                              fontSize: '0.8rem',
                              color: 'rgba(255, 255, 255, 0.9)'
                            }}>
                              🔗 Свързано с устройство ID: <strong style={{ color: '#333' }}>{zone.device_id}</strong>
                            </div>
                          )}
                          
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {!zone.device_id ? (
                              <button
                                className="btn btn-success btn-small"
                                onClick={() => openLinkingModal(zone, 'sprinkler-zone')}
                                style={{ flex: 1, fontSize: '0.85rem' }}
                              >
                                🔗 Свържи с устройство
                              </button>
                            ) : (
                              <>
                                <button
                                  className="btn btn-secondary btn-small"
                                  onClick={() => openLinkingModal(zone, 'sprinkler-zone')}
                                  style={{ flex: 1, fontSize: '0.85rem' }}
                                >
                                  🔄 Смени устройство
                                </button>
                                <button
                                  className="btn btn-danger btn-small"
                                  onClick={async () => {
                                    if (window.confirm(`Are you sure you want to unlink "${zone.name}"?`)) {
                                      setStatus({ message: 'Unlink feature will be added soon', type: 'info' });
                                    }
                                  }}
                                  style={{ fontSize: '0.85rem' }}
                                >
                                  ❌ Прекъсни
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Евакуационни врати */}
                {evacDoors.length > 0 && (
                  <div>
                    <h6 style={{ 
                      color: '#333', 
                      fontSize: '1rem', 
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      🚪 Evacuation doors ({evacDoors.length})
                    </h6>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
                      {evacDoors.map((door) => (
                        <div key={`manual-door-${door.id}`} style={{ 
                          background: door.device_id ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.05)', 
                          padding: '15px', 
                          borderRadius: '10px',
                          border: door.device_id ? '2px solid rgba(76, 175, 80, 0.4)' : '2px solid rgba(255, 255, 255, 0.2)',
                          transition: 'all 0.3s ease'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ color: '#333', fontWeight: '600', fontSize: '0.95rem', marginBottom: '6px' }}>
                                🚪 {door.name}
                              </div>
                              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', marginBottom: '4px' }}>
                                📍 {door.location || 'Unspecified'}
                              </div>
                              <div style={{ 
                                color: door.state === 'ALARM' ? '#ff4444' : door.state === 'SUPERVISORY' ? '#ffaa00' : '#44ff44',
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                              }}>
                                📊 {door.state || 'NORMAL'}
                              </div>
                            </div>
                            <div style={{ 
                              padding: '4px 10px', 
                              borderRadius: '12px',
                              background: door.device_id ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              color: door.device_id ? '#4CAF50' : '#FF9800'
                            }}>
                              {door.device_id ? '✅ Свързано' : '⚠️ Несвързано'}
                            </div>
                          </div>
                          
                          {door.device_id && (
                            <div style={{ 
                              background: 'rgba(255, 255, 255, 0.1)', 
                              padding: '8px', 
                              borderRadius: '6px',
                              marginBottom: '10px',
                              fontSize: '0.8rem',
                              color: 'rgba(255, 255, 255, 0.9)'
                            }}>
                              🔗 Свързано с устройство ID: <strong style={{ color: '#333' }}>{door.device_id}</strong>
                            </div>
                          )}
                          
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {!door.device_id ? (
                              <button
                                className="btn btn-success btn-small"
                                onClick={() => openLinkingModal(door, 'evac-door')}
                                style={{ flex: 1, fontSize: '0.85rem' }}
                              >
                                🔗 Свържи с устройство
                              </button>
                            ) : (
                              <>
                                <button
                                  className="btn btn-secondary btn-small"
                                  onClick={() => openLinkingModal(door, 'evac-door')}
                                  style={{ flex: 1, fontSize: '0.85rem' }}
                                >
                                  🔄 Смени устройство
                                </button>
                                <button
                                  className="btn btn-danger btn-small"
                                  onClick={async () => {
                                    if (window.confirm(`Are you sure you want to unlink "${door.name}"?`)) {
                                      setStatus({ message: 'Unlink feature will be added soon', type: 'info' });
                                    }
                                  }}
                                  style={{ fontSize: '0.85rem' }}
                                >
                                  ❌ Прекъсни
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Device List */}
          {devices.length > 0 && (
            <div className="device-list">
              <h4>📱 Открити устройства</h4>
              <div className="devices-grid">
                {devices.map((device) => (
                  <div key={device.id} className={`device-item ${device.status}`}>
                    <div className="device-item-header">
                      <span className="device-item-icon">
                        {device.device_type === 'smoke_detector' && '💨'}
                        {device.device_type === 'heat_sensor' && '🌡️'}
                        {device.device_type === 'camera' && '📷'}
                        {device.device_type === 'sprinkler' && '💧'}
                        {device.device_type === 'alarm' && '🔔'}
                        {device.device_type === 'door_sensor' && '🚪'}
                        {device.device_type === 'electrical_panel' && '⚡'}
                        {!['smoke_detector', 'heat_sensor', 'camera', 'sprinkler', 'alarm', 'door_sensor', 'electrical_panel'].includes(device.device_type) && '📱'}
                      </span>
                      <span className="device-item-name">{device.device_name}</span>
                      <span className={`device-item-status ${device.status}`}>
                        {device.status === 'online' && '🟢'}
                        {device.status === 'offline' && '🔴'}
                        {device.status === 'alarm' && '🚨'}
                      </span>
                    </div>
                    
                    <div className="device-item-info">
                      <div className="device-info-row">
                        <span className="info-label">📍 Локация:</span>
                        <span className="info-value">{device.location || 'Unspecified'}</span>
                      </div>
                      <div className="device-info-row">
                        <span className="info-label">🔧 Тип:</span>
                        <span className="info-value">{device.device_type}</span>
                      </div>
                      {device.qr_code && (
                        <div className="device-info-row">
                          <span className="info-label">🔲 QR:</span>
                          <span className="info-value">{device.qr_code}</span>
                        </div>
                      )}
                    </div>

                    <div className="device-item-actions">
                      {device.status === 'online' ? (
                        <button
                          className="btn btn-danger btn-small"
                          onClick={() => disconnectDevice(device.id)}
                        >
                          🔌 Изключи
                        </button>
                      ) : (
                        <button
                          className="btn btn-success btn-small"
                          onClick={() => connectDevice(device.id)}
                        >
                          🔌 Свържи
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {devices.length === 0 && !deviceScanStatus.scanning && (
            <div className="no-devices">
              <span className="no-devices-icon">📭</span>
              <span className="no-devices-text">Няма открити устройства</span>
              <span className="no-devices-hint">Натиснете "Сканирай за устройства" за да търсите нови устройства в мрежата</span>
            </div>
                )}
        </div>
            )}

      {/* Device Creation Modal - извън activeSection условието */}
      {deviceCreationModal.open && (
        <div className="modal-overlay modal-device-creation" onClick={closeDeviceCreationModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Добавяне на ново устройство</h3>
              <button className="modal-close" onClick={closeDeviceCreationModal}>✖</button>
            </div>
            
            <div className="modal-body">
              {deviceCreationModal.forLinking && (
                <div className="modal-info">
                  <p>🔗 Устройството ще бъде автоматично свързано с: <strong>{linkingModal.equipment?.name}</strong></p>
                </div>
              )}

              <div className="device-form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Име на устройството *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newDevice.name}
                      onChange={(e) => setNewDevice(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="напр. Детектор дим - Офис 201"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Тип устройство *</label>
                    <select
                      className="form-input"
                      value={newDevice.type}
                      onChange={(e) => setNewDevice(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="smoke_detector">💨 Детектор за дим</option>
                      <option value="heat_sensor">🌡️ Температурен сензор</option>
                      <option value="camera">📷 Камера</option>
                      <option value="alarm">🔔 Алармена система</option>
                      <option value="sprinkler">💧 Спринклер</option>
                      <option value="door_sensor">🚪 Сензор за врата</option>
                      <option value="motion_sensor">👋 Сензор за движение</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Локация *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newDevice.location}
                      onChange={(e) => setNewDevice(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="напр. Етаж 2, Офис 201"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">IP адрес на устройството</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newDevice.ip_address}
                      onChange={(e) => setNewDevice(prev => ({ ...prev, ip_address: e.target.value }))}
                      placeholder="напр. 192.168.1.50"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Тип връзка</label>
                    <select
                      className="form-input"
                      value={newDevice.connection_type}
                      onChange={(e) => setNewDevice(prev => ({ ...prev, connection_type: e.target.value }))}
                    >
                      <option value="wifi">Wi‑Fi</option>
                      <option value="ethernet">Ethernet (LAN)</option>
                      <option value="lte">LTE / мобилен интернет</option>
                      <option value="other">Друг вид връзка</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Модел</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newDevice.model}
                      onChange={(e) => setNewDevice(prev => ({ ...prev, model: e.target.value }))}
                      placeholder="напр. FireDetect Pro X200"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Сериен номер</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newDevice.serial_number}
                      onChange={(e) => setNewDevice(prev => ({ ...prev, serial_number: e.target.value }))}
                      placeholder="напр. SN123456789"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Дата на инсталация</label>
                    <input
                      type="date"
                      className="form-input"
                      value={newDevice.installation_date}
                      onChange={(e) => setNewDevice(prev => ({ ...prev, installation_date: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Начално ниво на батерия (%)</label>
                    <input
                      type="number"
                      className="form-input"
                      value={newDevice.battery_level}
                      onChange={(e) => setNewDevice(prev => ({ ...prev, battery_level: parseInt(e.target.value) }))}
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label className="form-label">Забележки</label>
                    <textarea
                      className="form-input form-textarea"
                      value={newDevice.notes}
                      onChange={(e) => setNewDevice(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Допълнителна информация за устройството..."
                      rows="3"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeDeviceCreationModal}>
                Откажи
              </button>
              <button
                className="btn btn-success"
                onClick={createNewDevice}
                disabled={!newDevice.name || !newDevice.location}
              >
                {deviceCreationModal.forLinking ? '➕ Създай и свържи' : '➕ Създай устройство'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Информационен модал за техника */}
      {infoModal.open && (
        <div 
          className="modal-overlay"
          onClick={() => setInfoModal(prev => ({ ...prev, open: false }))}
        >
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{infoModal.title}</h3>
              <button
                className="modal-close"
                onClick={() => setInfoModal(prev => ({ ...prev, open: false }))}
              >
                ✖
              </button>
            </div>
            <div className="modal-body">
              {infoModal.description && (
                <p style={{ marginBottom: '10px', color: '#333' }}>
                  {infoModal.description}
                </p>
              )}
              {infoModal.body && (
                <p style={{ marginBottom: '0', color: '#333', whiteSpace: 'pre-line' }}>
                  {infoModal.body}
                </p>
              )}
            </div>
            <div className="modal-footer">
              {['electrical-panel', 'sprinkler-zone', 'evac-door'].includes(infoModal.type) && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const type = infoModal.type;
                    setInfoModal(prev => ({ ...prev, open: false }));
                    quickLinkByType(type);
                  }}
                >
                  Продължи към свързване
                </button>
              )}
              <button
                className="btn btn-secondary"
                style={{ marginLeft: '8px' }}
                onClick={() => setInfoModal(prev => ({ ...prev, open: false }))}
              >
                Затвори
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Device Linking Modal - извън activeSection условието */}
      {linkingModal.open && (
        <div 
          className="modal-overlay" 
          style={{ 
            display: deviceCreationModal.open ? 'none' : 'flex' 
          }}
          onClick={(e) => {
            // Не затваряй ако device creation modal е отворен
            if (!deviceCreationModal.open) {
              closeLinkingModal();
            }
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔗 Свързване на техника с устройство</h3>
              <button 
                className="modal-close" 
                onClick={(e) => {
                  e.stopPropagation();
                  closeLinkingModal();
                }}
              >
                ✖
              </button>
            </div>
            
            <div className="modal-body">
              <div className="modal-info">
                <h4>{linkingModal.equipment?.name}</h4>
                <p>📍 {linkingModal.equipment?.location}</p>
                <p>Тип: <strong>{linkingModal.equipmentType}</strong></p>
              </div>

              <div className="modal-section">
                <div className="modal-section-header">
                  <h4>Налични устройства за свързване:</h4>
                  <button
                    className="btn btn-primary btn-small"
                    onClick={() => {
                      openDeviceCreationModal(true, linkingModal.equipment);
                    }}
                  >
                    ➕ Добави ново устройство
                  </button>
                </div>
                
                {linkingModal.availableDevices.length === 0 ? (
                  <div className="no-devices">
                    <p>⚠️ Няма налични устройства</p>
                    <p className="hint">Натиснете "Добави ново устройство" за да създадете</p>
                  </div>
                ) : (
                  <>
                    {/* Search field */}
                    <div className="device-search">
                      <input
                        type="text"
                        className="search-input"
                        placeholder="🔍 Търси устройство по име, локация или тип..."
                        value={deviceSearchTerm}
                        onChange={(e) => setDeviceSearchTerm(e.target.value)}
                      />
                      {deviceSearchTerm && (
                        <button
                          className="search-clear"
                          onClick={() => setDeviceSearchTerm('')}
                        >
                          ✖
                        </button>
                      )}
                    </div>

                    <div className="device-list-modal">
                      {filteredDevices.length === 0 ? (
                        <div className="no-devices">
                          <p>🔍 Няма резултати от търсенето</p>
                          <p className="hint">Опитайте с различни ключови думи</p>
                        </div>
                      ) : (
                        filteredDevices.map((device) => (
                          <div key={device.id} className="device-option">
                            <div className="device-option-info">
                              <span className="device-icon">
                                {device.device_type === 'smoke_detector' && '💨'}
                                {device.device_type === 'heat_sensor' && '🌡️'}
                                {device.device_type === 'camera' && '📷'}
                                {device.device_type === 'alarm' && '🔔'}
                                {device.device_type === 'sprinkler' && '💧'}
                                {!['smoke_detector', 'heat_sensor', 'camera', 'alarm', 'sprinkler'].includes(device.device_type) && '📱'}
                              </span>
                              <div>
                                <div className="device-name">{device.name || device.device_name}</div>
                                <div className="device-details">
                                  {device.device_type || device.type} | {device.location || 'Unknown location'}
                                </div>
                              </div>
                            </div>
                            <button
                              className="btn btn-success btn-small"
                              onClick={async (e) => {
                                e.stopPropagation(); // Предотврати event bubbling
                                console.log('🔘 [BUTTON] Свържи бутон натиснат за device:', device.id);
                                await linkEquipmentToDevice(device.id, 'manual');
                              }}
                            >
                              Свържи
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    
                    <div className="device-count-info">
                      Показани: {filteredDevices.length} от {linkingModal.availableDevices.length} устройства
                    </div>
                  </>
                )}
              </div>

              <div className="modal-section">
                <h4>Режим на свързване:</h4>
                <div className="link-mode-options">
                  <button
                    className="btn btn-secondary"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (linkingModal.availableDevices.length > 0) {
                        console.log('🤖 [AUTO] Автоматично свързване с първо устройство');
                        await linkEquipmentToDevice(linkingModal.availableDevices[0].id, 'auto');
                      }
                    }}
                    disabled={linkingModal.availableDevices.length === 0}
                  >
                    🤖 Автоматично (първо налично)
                  </button>
                  <span className="mode-hint">или изберете устройство от списъка</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={(e) => {
                  e.stopPropagation();
                  closeLinkingModal();
                }}
              >
                Откажи
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AISettings;
